import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgMapsViewComponent } from '@ng-maps/core';
import { GoogleComponent } from '@ng-maps/google';
import { Subject } from 'rxjs';
import AddressHelper from 'src/app/helpers/address-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import ProjectMapHelper from 'src/app/helpers/project-map-helper';
import { Tour } from 'src/app/model/tour/tour';
import {
  EmployeeTourMapHelper,
  EmployeeTourMapMarker,
} from 'src/app/pages/immo-ip/tour/tour-page/employee-tour-edit-modal/employee-tour-map/helper/employee-tour-map-helper';
import {
  CoordinateLike,
  EmployeeTourMapDirectionsCalculationService,
} from 'src/app/pages/immo-ip/tour/tour-page/employee-tour-edit-modal/employee-tour-map/service/employee-tour-map-directions-calculation.service';
import { GeocoderService } from 'src/app/services/geocoder.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';

@Component({
  selector: 'immo-ip-placer-tour-route-map',
  templateUrl: './immo-ip-placer-tour-route-map.component.html',
  styleUrls: ['./immo-ip-placer-tour-route-map.component.scss'],
})
export class ImmoIPPlacerTourRouteMapComponent
  extends SubscriptionBasedComponent
  implements OnInit, AfterViewInit
{
  @Input()
  tour: Tour;
  @Input()
  tourDateChangedOrDestroy: Subject<any>;
  @Input()
  rerenderMap: Subject<any>;

  INPRESS_COORDINATES = ProjectMapHelper.INPRESS_COORDINATES;
  HIDE_POI_STYLE = ProjectMapHelper.HIDE_POI_STYLE;

  @ViewChild(GoogleComponent, { static: true })
  agmMap: NgMapsViewComponent<any>;

  markers: EmployeeTourMapMarker[];
  totalDistance: string;

  private directionsRenderer = new google.maps.DirectionsRenderer({
    preserveViewport: true,
    suppressMarkers: true,
    suppressInfoWindows: true,
  });

  constructor(
    private employeeTourMapDirectionsCalculationService: EmployeeTourMapDirectionsCalculationService,
    private geocoderService: GeocoderService
  ) {
    super();
  }

  ngOnInit(): void {
    this.processTourItems();
    if (this.rerenderMap != null) {
      this.addSubscription(this.rerenderMap, () => {
        this.processTourItems();
      });
    }
  }

  ngAfterViewInit(): void {
    this.addSubscription(
      this.agmMap.mapReady,
      (map: google.maps.Map) => {
        this.directionsRenderer.setMap(map);
      },
      GeneralHelper.noop
    );
  }

  private processTourItems(): void {
    const items =
      this.tour == null || !CollectionHelper.isPopulated(this.tour.tourItems)
        ? null
        : this.tour.tourItems.map((ti) =>
            ti.subProject == null && ti.immoTask == null ? null : ti
          );
    CollectionHelper.filterInPlace(items, (s) => s != null);
    this.markers = EmployeeTourMapHelper.getEmployeeTourMapMarkers(items, true);

    this.directionsRenderer.setDirections({
      geocoded_waypoints: [],
      routes: [],
    });

    if (CollectionHelper.isPopulated(this.markers)) {
      this.renderDirections();
    }
  }

  private async renderDirections(): Promise<void> {
    let placerAddressCoordinates = null;
    try {
      placerAddressCoordinates =
        this.tour == null ||
        this.tour.placer == null ||
        this.tour.placer.address == null
          ? null
          : await this.geocoderService.geocodeAddress(
              AddressHelper.getAddressDisplay(this.tour.placer.address)
            );
    } catch (error) {
      console.log(error);
    }
    const toRender: CoordinateLike[] =
      placerAddressCoordinates == null
        ? this.markers
        : [
            {
              lat: placerAddressCoordinates.lat,
              lng: placerAddressCoordinates.lng,
            },
            ...this.markers,
          ];
    this.employeeTourMapDirectionsCalculationService
      .calculateDirections(toRender, this.tourDateChangedOrDestroy)
      .then((result: google.maps.DirectionsResult) => {
        this.calculateTotalDistance(result);
        return this.directionsRenderer.setDirections(result);
      })
      .catch((err) => console.error(err));
  }

  private calculateTotalDistance(result: google.maps.DirectionsResult): void {
    let total = 0;
    result.routes[0].legs.forEach((l) => {
      total += l.distance.value;
    });
    let roundedKm = (total * 0.001).toFixed(2);
    this.totalDistance = roundedKm.replace('.00', '').replace('.', ',') + ' km';
  }
}
